/**
 * @fileoverview Live Stadium Split-Screen 1 vs 1 Game Stage Component for "Memorama" (Memory) (/screen).
 * Implements a Zero-Canvas architecture with GPU Layer Compositing (60 FPS, 0% CPU in idle),
 * Split-Screen 1 vs 1 arena layout (J1 vs J2), 3D flip card grids with dynamic themes,
 * live telemetry synchronization, dynamic neon traffic-light stopwatch, arcade countdown overlay,
 * preview memorization banner, 3D metallic victory/defeat stamps, and ephemeral hardware-accelerated
 * confetti with guaranteed DOM auto-destruction (3000ms).
 * 
 * @module components/Screen/Sections/Memory/MemoryGame
 */

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
    GetConfig,
    GetTheme,
    preloadImages,
    preloadThemeOnce,
    playFlipSound,
    playMatchSuccessSound,
    playMatchFailedSound,
    playVictoryFanfare,
    playCountdownBeep
} from '../../../../utilities/BL/Memory';

/**
 * Default fallback colors and configuration when theme is not provided.
 * @constant {Object}
 */
const DEFAULT_GAME_THEME = {
    gameBackgroundType: 'color',
    gameBackgroundColor: '#0a0f1d',
    gameBackgroundUrl: '',
    gameTextColor: '#ffffff',
    player1Name: 'Jugador 1',
    player1Color: '#fbbf24',
    player1Avatar: '',
    player1Winner: '',
    player2Name: 'Jugador 2',
    player2Color: '#60a5fa',
    player2Avatar: '',
    player2Winner: '',
    cardBackBackgroundColor: '#1e293b',
    cardBackBackgroundType: 'color',
    cardBackBackgroundUrl: '',
    cardBackLogoUrl: '',
    fontFamily: 'Inter',
    pairsCount: 6,
    roundDurationSeconds: 60,
    previewTimeSeconds: 3,
    hideUnmatchedDelayMs: 800,
    brandLogoUrl: '',
    sponsorLogoUrl: ''
};

/**
 * Confetti colors palette for victory explosion.
 * @constant {Array<string>}
 */
const CONFETTI_COLOR_PALETTE = ['#fbbf24', '#60a5fa', '#34d399', '#f43f5e', '#a855f7', '#38bdf8', '#ffffff'];

/**
 * Fallback avatar SVG inmutable.
 * @constant {string}
 */
const DEFAULT_AVATAR_P1_SVG = `data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><circle cx="50" cy="50" r="45" fill="%231e293b" stroke="%23fbbf24" stroke-width="4"/><text x="50" y="58" font-family="Arial,sans-serif" font-size="28" font-weight="bold" fill="%23fbbf24" text-anchor="middle">J1</text></svg>`;
const DEFAULT_AVATAR_P2_SVG = `data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><circle cx="50" cy="50" r="45" fill="%231e293b" stroke="%2360a5fa" stroke-width="4"/><text x="50" y="58" font-family="Arial,sans-serif" font-size="28" font-weight="bold" fill="%2360a5fa" text-anchor="middle">J2</text></svg>`;
const DEFAULT_FALLBACK_LOGO_SVG = `data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><circle cx="50" cy="50" r="45" fill="%231e293b" stroke="%233b82f6" stroke-width="4"/><text x="50" y="58" font-family="Arial,sans-serif" font-size="28" font-weight="bold" fill="%23ffffff" text-anchor="middle">★</text></svg>`;

/**
 * Sanitiza nombres de contendientes para visualización limpia en estadio.
 * 
 * @param {string|null|undefined} text - Texto a sanitizar.
 * @param {string} fallback - Valor por defecto.
 * @param {number} [maxLength=24] - Longitud máxima permitida.
 * @returns {string} Texto seguro y recortado.
 */
const sanitizePlayerName = (text, fallback = 'Jugador', maxLength = 24) => {
    if (typeof text !== 'string') return fallback;
    const clean = text.replace(/[\x00-\x1F\x7F]/g, '').trim();
    return clean.length > 0 ? clean.slice(0, maxLength) : fallback;
};

/**
 * Genera el arreglo inicial de cartas para una cuadrícula.
 * 
 * @param {number} totalCards - Total de cartas (pares * 2).
 * @returns {Array<Object>} Arreglo de cartas inicializado.
 */
const createInitialCards = (totalCards = 12) => {
    return Array.from({ length: totalCards }, (_, idx) => ({
        cardIndex: idx,
        pairId: null,
        imageUrl: null,
        isFlipped: false,
        isMatched: false
    }));
};

/**
 * MemoryGame Component - Pantalla Gigante de Estadio para Memorama 1 vs 1
 * 
 * @function MemoryGame
 * @param {Object} props - Propiedades del componente.
 * @param {Object} [props.payload] - Paquete WebSocket SignalR.
 * @param {Object} [props.currentMatchState] - Estado de partida inyectado directamente por Screen.js.
 * @param {Object} [props.theme] - Tema visual inyectado directamente.
 * @param {Object} [props.config] - Configuración de sala inyectada.
 * @param {string} [props.roomId] - Identificador GUID de la sala.
 * @returns {React.ReactElement} Escenario de estadio y split-screen renderizado.
 */
export const MemoryGame = ({
    payload,
    currentMatchState,
    theme: propTheme,
    config: propConfig,
    roomId
}) => {
    // =========================================================================
    // 1. ESTADO DEL TEMA Y CONFIGURACIÓN VISUAL
    // =========================================================================
    const [activeThemeData, setActiveThemeData] = useState(() => {
        const initial = propTheme || propConfig || currentMatchState?.theme || null;
        return initial ? { ...DEFAULT_GAME_THEME, ...initial } : DEFAULT_GAME_THEME;
    });

    const isMountedRef = useRef(true);

    useEffect(() => {
        isMountedRef.current = true;
        return () => { isMountedRef.current = false; };
    }, []);

    /**
     * Resuelve rutas de archivos multimedia a URLs absolutas completas.
     */
    const resolveMediaUrl = useCallback((mediaPath) => {
        if (!mediaPath) return '';
        if (
            mediaPath.startsWith('http://') ||
            mediaPath.startsWith('https://') ||
            mediaPath.startsWith('blob:') ||
            mediaPath.startsWith('data:')
        ) {
            return mediaPath;
        }
        let apiBaseEndpointUrl = (process.env.REACT_APP_API_URL || 'https://localhost:50586/api/').replace('/api/', '/').replace('/api', '/');
        
        if (typeof window !== 'undefined' && window.location && window.location.hostname && window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1') {
            if (apiBaseEndpointUrl.includes('localhost') || apiBaseEndpointUrl.includes('127.0.0.1')) {
                apiBaseEndpointUrl = apiBaseEndpointUrl.replace('localhost', window.location.hostname).replace('127.0.0.1', window.location.hostname);
            }
            if (window.location.protocol === 'http:' && apiBaseEndpointUrl.startsWith('https://')) {
                apiBaseEndpointUrl = apiBaseEndpointUrl.replace('https://', 'http://');
            }
        }

        const sanitizedMediaPath = mediaPath.startsWith('/') ? mediaPath.substring(1) : mediaPath;
        return `${apiBaseEndpointUrl}${sanitizedMediaPath}`;
    }, []);

    /**
     * Carga el tema y configuración de la sala desde la API si no fueron suministrados vía props.
     */
    const fetchActiveThemeConfiguration = useCallback(async () => {
        if (!roomId) return;
        try {
            const configResponse = await GetConfig(roomId);
            if (!isMountedRef.current) return;

            if (configResponse && configResponse.success && configResponse.data) {
                const roomConfig = configResponse.data;
                if (roomConfig.activeTheme) {
                    setActiveThemeData((prev) => ({ ...prev, ...roomConfig.activeTheme }));
                    preloadThemeOnce(roomConfig.activeTheme);
                } else if (roomConfig.activeThemeId) {
                    const themeResponse = await GetTheme(roomConfig.activeThemeId, roomId);
                    if (!isMountedRef.current) return;
                    if (themeResponse && themeResponse.success && themeResponse.data) {
                        setActiveThemeData((prev) => ({ ...prev, ...themeResponse.data }));
                        preloadThemeOnce(themeResponse.data);
                    }
                }
            }
        } catch (fetchError) {
            console.warn('[MemoryGame] Error al cargar configuración remota:', fetchError);
        }
    }, [roomId]);

    // Sincronización continua del tema visual
    useEffect(() => {
        if (propTheme) {
            const directTheme = propTheme.theme && typeof propTheme.theme === 'object' ? propTheme.theme : propTheme;
            setActiveThemeData((prev) => ({ ...prev, ...directTheme }));
            preloadThemeOnce(directTheme);
            return;
        }

        if (propConfig) {
            const confTheme = propConfig.theme || propConfig.activeTheme || propConfig;
            if (confTheme) {
                setActiveThemeData((prev) => ({ ...prev, ...confTheme }));
                preloadThemeOnce(confTheme);
                return;
            }
        }

        if (payload) {
            const incomingTheme = payload.theme ||
                payload.state?.theme ||
                payload.data?.theme ||
                payload.payload?.theme ||
                payload.state?.currentTheme;

            if (incomingTheme) {
                setActiveThemeData((prev) => ({ ...prev, ...incomingTheme }));
                preloadThemeOnce(incomingTheme);
                return;
            }
        }

        fetchActiveThemeConfiguration();
    }, [payload, propTheme, propConfig, fetchActiveThemeConfiguration]);

    // =========================================================================
    // 2. ESTADO AUTORITATIVO DE LA PARTIDA Y TABLEROS
    // =========================================================================
    const pairsCount = activeThemeData?.pairsCount || activeThemeData?.PairsCount || 6;
    const totalCards = pairsCount * 2;

    const [gameState, setGameState] = useState(() => ({
        phase: 'game_ready', // 'idle' | 'cover' | 'game_ready' | 'countdown' | 'preview' | 'playing' | 'finished' | 'paused'
        remainingSeconds: activeThemeData?.roundDurationSeconds || activeThemeData?.RoundDurationSeconds || 60,
        countdownNumber: null,
        previewRemainingSeconds: activeThemeData?.previewTimeSeconds || activeThemeData?.PreviewTimeSeconds || 3,
        player1: {
            name: activeThemeData?.player1Name || 'Jugador 1',
            score: 0,
            totalFlips: 0,
            board: createInitialCards(totalCards)
        },
        player2: {
            name: activeThemeData?.player2Name || 'Jugador 2',
            score: 0,
            totalFlips: 0,
            board: createInitialCards(totalCards)
        },
        winner: null, // 'player1' | 'player2' | 'tie' | null
        winningReason: ''
    }));

    // =========================================================================
    // 3. EFECTOS VISUALES Y CINEMÁTICAS (CONFETTI 3s ZERO MEMORY LEAKS)
    // =========================================================================
    const [confettiList, setConfettiList] = useState([]);
    const confettiTimerRef = useRef(null);
    const unflipTimerP1Ref = useRef(null);
    const unflipTimerP2Ref = useRef(null);
    const previousPhaseRef = useRef('game_ready');

    /**
     * Genera la explosión de confeti efímero con auto-destrucción garantizada a los 3000ms.
     */
    const triggerConfettiExplosion = useCallback(() => {
        if (confettiTimerRef.current) {
            clearTimeout(confettiTimerRef.current);
            confettiTimerRef.current = null;
        }

        const pieces = [];
        for (let i = 0; i < 40; i++) {
            const startLeft = Math.random() * 100;
            const size = Math.random() * 14 + 10; // 10px a 24px
            const duration = Math.random() * 0.8 + 2.0; // 2.0s a 2.8s
            const delay = Math.random() * 0.3; // 0s a 0.3s
            const drift = Math.random() * 24 - 12; // -12vw a 12vw
            const rotation = Math.random() * 720 + 360;
            const color = CONFETTI_COLOR_PALETTE[Math.floor(Math.random() * CONFETTI_COLOR_PALETTE.length)];

            pieces.push({
                id: i,
                style: {
                    '--left': `${startLeft}vw`,
                    '--size': `${size}px`,
                    '--color': color,
                    '--duration': `${duration}s`,
                    '--delay': `${delay}s`,
                    '--drift': `${drift}vw`,
                    '--rotation': `${rotation}deg`
                }
            });
        }

        setConfettiList(pieces);

        // Auto-destrucción estricta tras 3 segundos para garantizar Zero Memory Leaks
        confettiTimerRef.current = setTimeout(() => {
            if (isMountedRef.current) {
                setConfettiList([]);
            }
            confettiTimerRef.current = null;
        }, 3000);
    }, []);

    // Limpieza de timers al desmontar
    useEffect(() => {
        return () => {
            if (confettiTimerRef.current) clearTimeout(confettiTimerRef.current);
            if (unflipTimerP1Ref.current) clearTimeout(unflipTimerP1Ref.current);
            if (unflipTimerP2Ref.current) clearTimeout(unflipTimerP2Ref.current);
        };
    }, []);

    // =========================================================================
    // 4. MANEJO Y SINCRONIZACIÓN DE MENSAJES SIGNALR
    // =========================================================================
    const handleIncomingState = useCallback((stateObj, rawType = '') => {
        if (!stateObj || typeof stateObj !== 'object') return;

        setGameState((prevState) => {
            const matchState = stateObj.matchState || stateObj.state || stateObj;
            const typeLower = String(rawType || '').toLowerCase();
            const rawPhase = stateObj.phase || matchState.phase || stateObj.currentState || matchState.currentState || (
                typeLower.includes('countdown') ? 'countdown' :
                typeLower.includes('preview') ? 'preview' :
                typeLower.includes('started') || typeLower.includes('playing') ? 'playing' :
                typeLower.includes('finished') || typeLower.includes('game_over') ? 'finished' :
                typeLower.includes('game_ready') || typeLower.includes('reset') ? 'game_ready' :
                prevState.phase
            );
            const phase = String(rawPhase || '').toLowerCase();

            const remainingSeconds = stateObj.remainingSeconds !== undefined
                ? Math.max(0, Math.round(stateObj.remainingSeconds))
                : (matchState.remainingSeconds !== undefined
                    ? Math.max(0, Math.round(matchState.remainingSeconds))
                    : (stateObj.secondsRemaining !== undefined ? Math.max(0, Math.round(stateObj.secondsRemaining)) : prevState.remainingSeconds));

            const countdownNumber = stateObj.countdownSeconds ?? matchState.countdownSeconds ?? stateObj.count ?? (typeLower.includes('countdown') ? (stateObj.step ?? 3) : prevState.countdownNumber);
            const previewRemainingSeconds = stateObj.previewTimeSeconds ?? matchState.previewTimeSeconds ?? stateObj.previewSeconds ?? prevState.previewRemainingSeconds;

            const p1Incoming = matchState.player1 || matchState.Player1 || stateObj.player1 || stateObj.Player1 || {};
            const p2Incoming = matchState.player2 || matchState.Player2 || stateObj.player2 || stateObj.Player2 || {};

            const explicitWinner = stateObj.winner !== undefined ? stateObj.winner : (matchState.winner !== undefined ? matchState.winner : (stateObj.winnerSide !== undefined ? stateObj.winnerSide : null));
            const winner = (phase === 'countdown' || phase === 'game_ready' || phase === 'preview' || phase === 'playing') ? explicitWinner : (explicitWinner !== null ? explicitWinner : prevState.winner);
            const winningReason = (phase === 'countdown' || phase === 'game_ready' || phase === 'preview' || phase === 'playing') ? (stateObj.winningReason || matchState.winningReason || '') : (stateObj.winningReason || matchState.winningReason || stateObj.reason || matchState.reason || prevState.winningReason);

            // Nombres de contendientes
            const p1Name = sanitizePlayerName(p1Incoming.name || stateObj.player1Name || matchState.player1Name || activeThemeData?.player1Name, prevState.player1.name, 24);
            const p2Name = sanitizePlayerName(p2Incoming.name || stateObj.player2Name || matchState.player2Name || activeThemeData?.player2Name, prevState.player2.name, 24);

            // Sincronización dinámica de cantidad de pares
            const incomingPairs = stateObj.pairsCount ?? matchState.pairsCount ?? stateObj.PairsCount ?? matchState.PairsCount ?? stateObj.configuredPairs;
            if (incomingPairs && Number(incomingPairs) > 0 && Number(incomingPairs) !== activeThemeData?.pairsCount) {
                setActiveThemeData(prev => ({ ...prev, pairsCount: Number(incomingPairs) }));
            }

            const activePairsCount = incomingPairs && Number(incomingPairs) > 0 ? Number(incomingPairs) : (activeThemeData?.pairsCount || 6);
            const expectedTotalCards = activePairsCount * 2;

            // Tableros con preservación de caché de imágenes descubiertas
            let p1Board = prevState.player1.board;
            let p2Board = prevState.player2.board;

            if (Array.isArray(p1Incoming.board) && p1Incoming.board.length > 0) {
                p1Board = p1Incoming.board.map((c, idx) => ({
                    cardIndex: c.cardIndex ?? idx,
                    pairId: c.pairId ?? p1Board[idx]?.pairId ?? null,
                    imageUrl: c.imageUrl ? resolveMediaUrl(c.imageUrl) : (p1Board[idx]?.imageUrl || null),
                    isFlipped: Boolean(c.isFlipped),
                    isMatched: Boolean(c.isMatched)
                }));
            } else if (p1Board.length !== expectedTotalCards) {
                p1Board = createInitialCards(expectedTotalCards);
            }

            if (Array.isArray(p2Incoming.board) && p2Incoming.board.length > 0) {
                p2Board = p2Incoming.board.map((c, idx) => ({
                    cardIndex: c.cardIndex ?? idx,
                    pairId: c.pairId ?? p2Board[idx]?.pairId ?? null,
                    imageUrl: c.imageUrl ? resolveMediaUrl(c.imageUrl) : (p2Board[idx]?.imageUrl || null),
                    isFlipped: Boolean(c.isFlipped),
                    isMatched: Boolean(c.isMatched)
                }));
            } else if (p2Board.length !== expectedTotalCards) {
                p2Board = createInitialCards(expectedTotalCards);
            }

            return {
                phase,
                remainingSeconds,
                countdownNumber: phase === 'countdown' ? countdownNumber : null,
                previewRemainingSeconds,
                player1: {
                    name: p1Name,
                    score: p1Incoming.score ?? prevState.player1.score,
                    totalFlips: p1Incoming.totalFlips ?? prevState.player1.totalFlips,
                    board: p1Board
                },
                player2: {
                    name: p2Name,
                    score: p2Incoming.score ?? prevState.player2.score,
                    totalFlips: p2Incoming.totalFlips ?? prevState.player2.totalFlips,
                    board: p2Board
                },
                winner,
                winningReason
            };
        });
    }, [activeThemeData, resolveMediaUrl]);

    // =========================================================================
    // 4.1 PROCESADOR UNIFICADO DE MENSAJES SIGNALR Y ESTADO AUTORITATIVO
    // =========================================================================
    const processIncomingMessage = useCallback((messageObj) => {
        if (!messageObj || typeof messageObj !== 'object') return;

        const rawType = String(messageObj.type || messageObj.phase || messageObj.currentState || '').toLowerCase();
        const data = messageObj.payload !== undefined
            ? messageObj.payload
            : (messageObj.data !== undefined
                ? messageObj.data
                : (messageObj.lastFlipEvent || messageObj.lastMatchSuccessEvent || messageObj.lastCardsUnflippedEvent || messageObj.state || messageObj));

        switch (rawType) {
            case 'memory_countdown_started':
            case 'memory_countdown': {
                const count = data.countdownSeconds ?? messageObj.countdownSeconds ?? data.count ?? data.step ?? 3;
                const incomingPairs = data.pairsCount ?? data.PairsCount ?? messageObj.pairsCount ?? activeThemeData?.pairsCount ?? 6;
                const activePairs = incomingPairs && Number(incomingPairs) > 0 ? Number(incomingPairs) : (activeThemeData?.pairsCount || 6);
                if (activePairs !== activeThemeData?.pairsCount) {
                    setActiveThemeData(prev => ({ ...prev, pairsCount: activePairs }));
                }
                const totalC = activePairs * 2;
                setGameState(prev => ({
                    ...prev,
                    phase: 'countdown',
                    countdownNumber: count,
                    winner: null,
                    winningReason: '',
                    remainingSeconds: activeThemeData?.roundDurationSeconds || 60,
                    player1: {
                        ...prev.player1,
                        score: 0,
                        totalFlips: 0,
                        board: prev.player1.board.length === totalC
                            ? prev.player1.board.map(c => ({ ...c, isFlipped: false, isMatched: false }))
                            : createInitialCards(totalC)
                    },
                    player2: {
                        ...prev.player2,
                        score: 0,
                        totalFlips: 0,
                        board: prev.player2.board.length === totalC
                            ? prev.player2.board.map(c => ({ ...c, isFlipped: false, isMatched: false }))
                            : createInitialCards(totalC)
                    }
                }));
                if (confettiTimerRef.current) {
                    clearTimeout(confettiTimerRef.current);
                    confettiTimerRef.current = null;
                }
                setConfettiList([]);
                if (unflipTimerP1Ref.current) {
                    clearTimeout(unflipTimerP1Ref.current);
                    unflipTimerP1Ref.current = null;
                }
                if (unflipTimerP2Ref.current) {
                    clearTimeout(unflipTimerP2Ref.current);
                    unflipTimerP2Ref.current = null;
                }
                playCountdownBeep(count === 1);
                break;
            }

            case 'memory_preview_start':
            case 'memory_preview': {
                const previewSec = Number(data.previewTimeSeconds ?? messageObj.previewTimeSeconds ?? activeThemeData?.previewTimeSeconds ?? 3);
                const incomingPairs = data.pairsCount ?? data.PairsCount ?? messageObj.pairsCount ?? data.matchState?.pairsCount ?? activeThemeData?.pairsCount ?? 6;
                const activePairs = incomingPairs && Number(incomingPairs) > 0 ? Number(incomingPairs) : (activeThemeData?.pairsCount || 6);
                if (activePairs !== activeThemeData?.pairsCount) {
                    setActiveThemeData(prev => ({ ...prev, pairsCount: activePairs }));
                }
                setGameState(prev => {
                    const revealBoard = (board, incomingBoard) => {
                        if (Array.isArray(incomingBoard) && incomingBoard.length > 0) {
                            return incomingBoard.map((c, i) => ({
                                cardIndex: c.cardIndex ?? c.CardIndex ?? i,
                                pairId: c.pairId ?? c.PairId ?? null,
                                imageUrl: (c.imageUrl || c.ImageUrl) ? resolveMediaUrl(c.imageUrl || c.ImageUrl) : (board[i]?.imageUrl || null),
                                isFlipped: true,
                                isMatched: false
                            }));
                        }
                        return board.map(c => ({ ...c, isFlipped: true }));
                    };

                    const mState = messageObj.matchState || data.matchState || data.MatchState || messageObj.state || data.state || (data.player1 ? data : null) || (messageObj.player1 ? messageObj : null);
                    const p1IncomingBoard = mState?.player1?.board || mState?.player1?.Board || mState?.Player1?.Board || mState?.Player1?.board || data.player1?.board || data.player1Board || messageObj.player1?.board;
                    const p2IncomingBoard = mState?.player2?.board || mState?.player2?.Board || mState?.Player2?.Board || mState?.Player2?.board || data.player2?.board || data.player2Board || messageObj.player2?.board;

                    return {
                        ...prev,
                        phase: 'preview',
                        countdownNumber: null,
                        previewRemainingSeconds: previewSec,
                        player1: {
                            ...prev.player1,
                            board: revealBoard(prev.player1.board, p1IncomingBoard)
                        },
                        player2: {
                            ...prev.player2,
                            board: revealBoard(prev.player2.board, p2IncomingBoard)
                        }
                    };
                });
                break;
            }

            case 'memory_preview_tick': {
                if (data.previewTimeSeconds !== undefined || data.remainingSeconds !== undefined || messageObj.previewTimeSeconds !== undefined) {
                    const sec = data.previewTimeSeconds ?? messageObj.previewTimeSeconds ?? data.remainingSeconds;
                    setGameState(prev => ({ ...prev, countdownNumber: null, previewRemainingSeconds: sec }));
                }
                break;
            }

            case 'memory_preview_ended': {
                setGameState(prev => ({
                    ...prev,
                    phase: 'playing',
                    countdownNumber: null,
                    player1: {
                        ...prev.player1,
                        board: prev.player1.board.map(c => ({ ...c, isFlipped: Boolean(c.isMatched) }))
                    },
                    player2: {
                        ...prev.player2,
                        board: prev.player2.board.map(c => ({ ...c, isFlipped: Boolean(c.isMatched) }))
                    }
                }));
                break;
            }

            case 'memory_game_started':
            case 'memory_playing_started':
            case 'memory_start': {
                setGameState(prev => ({
                    ...prev,
                    phase: 'playing',
                    countdownNumber: null,
                    winner: null,
                    player1: {
                        ...prev.player1,
                        score: 0,
                        totalFlips: 0,
                        board: prev.player1.board.map(c => ({ ...c, isFlipped: false, isMatched: false }))
                    },
                    player2: {
                        ...prev.player2,
                        score: 0,
                        totalFlips: 0,
                        board: prev.player2.board.map(c => ({ ...c, isFlipped: false, isMatched: false }))
                    }
                }));
                break;
            }

            case 'memory_card_flipped': {
                const targetSide = String(data.playerSide || data.side || messageObj.playerSide || 'player1').toLowerCase();
                const cardIdx = Number(data.cardIndex ?? messageObj.cardIndex);
                const pairId = data.pairId ?? messageObj.pairId;
                const imageUrl = (data.imageUrl || messageObj.imageUrl) ? resolveMediaUrl(data.imageUrl || messageObj.imageUrl) : null;
                const totalFlips = data.totalFlips ?? messageObj.totalFlips;

                setGameState(prev => {
                    const isP2 = targetSide === 'player2' || targetSide === 'j2' || targetSide === 'p2' || targetSide === '2';
                    const playerKey = isP2 ? 'player2' : 'player1';
                    const targetPlayer = prev[playerKey];
                    const newBoard = [...targetPlayer.board];

                    if (newBoard[cardIdx]) {
                        newBoard[cardIdx] = {
                            ...newBoard[cardIdx],
                            isFlipped: true,
                            pairId: pairId ?? newBoard[cardIdx].pairId,
                            imageUrl: imageUrl ?? newBoard[cardIdx].imageUrl
                        };
                    }

                    return {
                        ...prev,
                        [playerKey]: {
                            ...targetPlayer,
                            totalFlips: totalFlips !== undefined ? totalFlips : targetPlayer.totalFlips + 1,
                            board: newBoard
                        }
                    };
                });

                playFlipSound();
                break;
            }

            case 'memory_match_success':
            case 'match_success': {
                const targetSide = String(data.playerSide || data.side || 'player1').toLowerCase();
                const c1 = Number(data.card1Index);
                const c2 = Number(data.card2Index);
                const newScore = data.score;
                const totalFlips = data.totalFlips;

                setGameState(prev => {
                    const isP2 = targetSide === 'player2' || targetSide === 'j2' || targetSide === 'p2' || targetSide === '2';
                    const playerKey = isP2 ? 'player2' : 'player1';
                    const targetPlayer = prev[playerKey];
                    const newBoard = [...targetPlayer.board];

                    if (newBoard[c1]) newBoard[c1] = { ...newBoard[c1], isMatched: true, isFlipped: true };
                    if (newBoard[c2]) newBoard[c2] = { ...newBoard[c2], isMatched: true, isFlipped: true };

                    return {
                        ...prev,
                        [playerKey]: {
                            ...targetPlayer,
                            score: newScore !== undefined ? newScore : targetPlayer.score + 1,
                            totalFlips: totalFlips !== undefined ? totalFlips : targetPlayer.totalFlips,
                            board: newBoard
                        }
                    };
                });

                playMatchSuccessSound();
                break;
            }

            case 'memory_match_failed':
            case 'match_failed': {
                const targetSide = String(data.playerSide || data.side || 'player1').toLowerCase();
                const c1 = Number(data.card1Index);
                const c2 = Number(data.card2Index);
                const totalFlips = data.totalFlips;
                const delayMs = activeThemeData?.hideUnmatchedDelayMs || 800;

                setGameState(prev => {
                    const isP2 = targetSide === 'player2' || targetSide === 'j2' || targetSide === 'p2' || targetSide === '2';
                    const playerKey = isP2 ? 'player2' : 'player1';
                    const targetPlayer = prev[playerKey];
                    return {
                        ...prev,
                        [playerKey]: {
                            ...targetPlayer,
                            totalFlips: totalFlips !== undefined ? totalFlips : targetPlayer.totalFlips
                        }
                    };
                });

                playMatchFailedSound();

                const isP2 = targetSide === 'player2' || targetSide === 'j2' || targetSide === 'p2' || targetSide === '2';
                const timerRef = isP2 ? unflipTimerP2Ref : unflipTimerP1Ref;
                if (timerRef.current) clearTimeout(timerRef.current);
                timerRef.current = setTimeout(() => {
                    setGameState(prev => {
                        const playerKey = isP2 ? 'player2' : 'player1';
                        const targetPlayer = prev[playerKey];
                        const newBoard = [...targetPlayer.board];
                        if (newBoard[c1] && !newBoard[c1].isMatched) newBoard[c1] = { ...newBoard[c1], isFlipped: false };
                        if (newBoard[c2] && !newBoard[c2].isMatched) newBoard[c2] = { ...newBoard[c2], isFlipped: false };
                        return {
                            ...prev,
                            [playerKey]: {
                                ...targetPlayer,
                                board: newBoard
                            }
                        };
                    });
                }, delayMs);
                break;
            }

            case 'memory_cards_unflipped':
            case 'cards_unflipped': {
                const targetSide = String(data.playerSide || data.side || 'player1').toLowerCase();
                const c1 = Number(data.card1Index);
                const c2 = Number(data.card2Index);

                setGameState(prev => {
                    const isP2 = targetSide === 'player2' || targetSide === 'j2' || targetSide === 'p2' || targetSide === '2';
                    const playerKey = isP2 ? 'player2' : 'player1';
                    const targetPlayer = prev[playerKey];
                    const newBoard = [...targetPlayer.board];

                    if (newBoard[c1] && !newBoard[c1].isMatched) newBoard[c1] = { ...newBoard[c1], isFlipped: false };
                    if (newBoard[c2] && !newBoard[c2].isMatched) newBoard[c2] = { ...newBoard[c2], isFlipped: false };

                    return {
                        ...prev,
                        [playerKey]: {
                            ...targetPlayer,
                            board: newBoard
                        }
                    };
                });
                break;
            }

            case 'memory_telemetry': {
                const remSec = data.remainingSeconds !== undefined
                    ? Math.max(0, Math.round(data.remainingSeconds))
                    : (data.matchState?.remainingSeconds !== undefined ? Math.max(0, Math.round(data.matchState.remainingSeconds)) : undefined);

                const p1 = data.player1 || data.matchState?.player1;
                const p2 = data.player2 || data.matchState?.player2;

                setGameState(prev => ({
                    ...prev,
                    ...(remSec !== undefined ? { remainingSeconds: remSec } : {}),
                    ...(data.phase ? { phase: String(data.phase).toLowerCase() } : {}),
                    ...(p1 ? {
                        player1: {
                            ...prev.player1,
                            ...(p1.name ? { name: sanitizePlayerName(p1.name, prev.player1.name, 24) } : {}),
                            ...(p1.score !== undefined ? { score: p1.score } : {}),
                            ...(p1.totalFlips !== undefined ? { totalFlips: p1.totalFlips } : {})
                        }
                    } : {}),
                    ...(p2 ? {
                        player2: {
                            ...prev.player2,
                            ...(p2.name ? { name: sanitizePlayerName(p2.name, prev.player2.name, 24) } : {}),
                            ...(p2.score !== undefined ? { score: p2.score } : {}),
                            ...(p2.totalFlips !== undefined ? { totalFlips: p2.totalFlips } : {})
                        }
                    } : {})
                }));
                break;
            }

            case 'memory_names_updated':
            case 'memory_update_names': {
                const p1NewName = data.player1Name || data.player1;
                const p2NewName = data.player2Name || data.player2;
                setGameState(prev => ({
                    ...prev,
                    player1: { ...prev.player1, name: p1NewName ? sanitizePlayerName(p1NewName, prev.player1.name, 24) : prev.player1.name },
                    player2: { ...prev.player2, name: p2NewName ? sanitizePlayerName(p2NewName, prev.player2.name, 24) : prev.player2.name }
                }));
                break;
            }

            case 'memory_game_finished':
            case 'memory_finished':
            case 'memory_game_over':
            case 'memory_all_pairs_matched':
            case 'all_pairs_matched': {
                const mState = messageObj.matchState || data.matchState || data.MatchState || messageObj.state || data.state || {};
                let winner = messageObj.winner || data.winner || mState.winner || messageObj.winnerSide || data.winnerSide || mState.winnerSide;
                const reason = messageObj.winningReason || data.winningReason || mState.winningReason || messageObj.reason || data.reason || mState.reason || 'game_finished';

                setGameState(prev => {
                    // Respaldo autoritativo por marcador si no viene un ganador explícito
                    let resolvedWinner = winner;
                    if (!resolvedWinner) {
                        const s1 = data.scoreJ1 ?? data.player1?.score ?? mState.player1?.score ?? prev.player1.score;
                        const s2 = data.scoreJ2 ?? data.player2?.score ?? mState.player2?.score ?? prev.player2.score;
                        if (s1 > s2) resolvedWinner = 'player1';
                        else if (s2 > s1) resolvedWinner = 'player2';
                        else resolvedWinner = 'tie';
                    }

                    return {
                        ...prev,
                        phase: 'finished',
                        winner: resolvedWinner,
                        winningReason: reason
                    };
                });
                break;
            }

            case 'memory_game_reset':
            case 'memory_reset':
            case 'memory_match_reset':
            case 'memory_game_ready':
            case 'memory_game_scene_shown':
            case 'memory_game_shown': {
                const incomingPairs = data.pairsCount ?? data.PairsCount ?? data.matchState?.pairsCount ?? activeThemeData?.pairsCount ?? 6;
                const activePairs = incomingPairs && Number(incomingPairs) > 0 ? Number(incomingPairs) : 6;
                const totalC = activePairs * 2;
                setGameState({
                    phase: 'game_ready',
                    remainingSeconds: activeThemeData?.roundDurationSeconds || 60,
                    countdownNumber: null,
                    previewRemainingSeconds: activeThemeData?.previewTimeSeconds || 3,
                    player1: {
                        name: activeThemeData?.player1Name || 'Jugador 1',
                        score: 0,
                        totalFlips: 0,
                        board: createInitialCards(totalC)
                    },
                    player2: {
                        name: activeThemeData?.player2Name || 'Jugador 2',
                        score: 0,
                        totalFlips: 0,
                        board: createInitialCards(totalC)
                    },
                    winner: null,
                    winningReason: ''
                });
                if (confettiTimerRef.current) {
                    clearTimeout(confettiTimerRef.current);
                    confettiTimerRef.current = null;
                }
                setConfettiList([]);
                if (unflipTimerP1Ref.current) {
                    clearTimeout(unflipTimerP1Ref.current);
                    unflipTimerP1Ref.current = null;
                }
                if (unflipTimerP2Ref.current) {
                    clearTimeout(unflipTimerP2Ref.current);
                    unflipTimerP2Ref.current = null;
                }
                break;
            }

            default:
                // Sincronización general de estado (snapshot completo / layout / cover)
                handleIncomingState(data, rawType);
                break;
        }
    }, [activeThemeData, totalCards, resolveMediaUrl, handleIncomingState]);

    // Procesar prop directas (currentMatchState o payload)
    useEffect(() => {
        if (currentMatchState && typeof currentMatchState === 'object') {
            processIncomingMessage(currentMatchState);
            return;
        }

        if (payload && typeof payload === 'object') {
            processIncomingMessage(payload);
        }
    }, [currentMatchState, payload, processIncomingMessage]);

    // Disparador de fanfarria y confeti ante victoria
    useEffect(() => {
        const currentPhase = gameState.phase;
        const prevPhase = previousPhaseRef.current;
        previousPhaseRef.current = currentPhase;

        if (currentPhase === 'finished' && prevPhase !== 'finished') {
            playVictoryFanfare();
            triggerConfettiExplosion();
        }
    }, [gameState.phase, triggerConfettiExplosion]);

    // =========================================================================
    // 5. EXTRACCIÓN DE ATRIBUTOS VISUALES Y REGLAS DE CUADRÍCULA
    // =========================================================================
    const gameBackgroundType = activeThemeData?.gameBackgroundType || activeThemeData?.GameBackgroundType || DEFAULT_GAME_THEME.gameBackgroundType;
    const gameBackgroundColor = activeThemeData?.gameBackgroundColor || activeThemeData?.GameBackgroundColor || DEFAULT_GAME_THEME.gameBackgroundColor;
    const rawGameBgUrl = activeThemeData?.gameBackgroundUrl || activeThemeData?.GameBackgroundUrl;
    const gameBackgroundUrl = rawGameBgUrl ? resolveMediaUrl(rawGameBgUrl) : '';
    const gameTextColor = activeThemeData?.gameTextColor || activeThemeData?.GameTextColor || DEFAULT_GAME_THEME.gameTextColor;

    const player1Color = activeThemeData?.player1Color || activeThemeData?.Player1Color || DEFAULT_GAME_THEME.player1Color;
    const player2Color = activeThemeData?.player2Color || activeThemeData?.Player2Color || DEFAULT_GAME_THEME.player2Color;
    const player1Avatar = activeThemeData?.player1Avatar || activeThemeData?.Player1Avatar ? resolveMediaUrl(activeThemeData?.player1Avatar || activeThemeData?.Player1Avatar) : '';
    const player2Avatar = activeThemeData?.player2Avatar || activeThemeData?.Player2Avatar ? resolveMediaUrl(activeThemeData?.player2Avatar || activeThemeData?.Player2Avatar) : '';

    const cardBackBackgroundColor = activeThemeData?.cardBackBackgroundColor || activeThemeData?.CardBackBackgroundColor || DEFAULT_GAME_THEME.cardBackBackgroundColor;
    const rawCardBackLogoUrl = activeThemeData?.cardBackLogoUrl || activeThemeData?.CardBackLogoUrl;
    const cardBackLogoUrl = rawCardBackLogoUrl ? resolveMediaUrl(rawCardBackLogoUrl) : '';

    const rawBrandLogo = activeThemeData?.brandLogoUrl || activeThemeData?.BrandLogoUrl;
    const brandLogoUrl = rawBrandLogo ? resolveMediaUrl(rawBrandLogo) : '';
    const rawSponsorLogo = activeThemeData?.sponsorLogoUrl || activeThemeData?.SponsorLogoUrl;
    const sponsorLogoUrl = rawSponsorLogo ? resolveMediaUrl(rawSponsorLogo) : '';

    const chosenFontFamily = activeThemeData?.fontFamily || activeThemeData?.FontFamily || 'Inter';

    // Determinar clase de cuadrícula según total de cartas
    const gridColumnsClass = useMemo(() => {
        if (pairsCount <= 4) return 'memory-grid-2x4'; // 8 cartas
        if (pairsCount <= 6) return 'memory-grid-3x4'; // 12 cartas
        if (pairsCount <= 8) return 'memory-grid-4x4'; // 16 cartas
        return 'memory-grid-4x4';
    }, [pairsCount]);

    // Semáforo dinámico del cronómetro (>10s verde, <=10s amarillo, <=5s rojo)
    const timerClass = useMemo(() => {
        const s = gameState.remainingSeconds;
        if (s <= 5) return 'timer-danger';
        if (s <= 10) return 'timer-warning';
        return 'timer-normal';
    }, [gameState.remainingSeconds]);

    // Formatear segundos en formato MM:SS
    const formattedStopwatch = useMemo(() => {
        const total = Math.max(0, gameState.remainingSeconds);
        const mins = Math.floor(total / 60);
        const secs = total % 60;
        return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
    }, [gameState.remainingSeconds]);

    // Determinar ganador con normalización y fallback matemático por marcador
    const normalizedWinner = String(gameState.winner || '').toLowerCase();
    const isP1Winner = normalizedWinner === 'player1' || normalizedWinner === 'j1' || normalizedWinner === 'p1' || (gameState.phase === 'finished' && gameState.player1.score > gameState.player2.score && (!gameState.winner || gameState.winner === 'tie'));
    const isP2Winner = normalizedWinner === 'player2' || normalizedWinner === 'j2' || normalizedWinner === 'p2' || (gameState.phase === 'finished' && gameState.player2.score > gameState.player1.score && (!gameState.winner || gameState.winner === 'tie'));
    const isTie = (normalizedWinner === 'tie' || normalizedWinner === 'empate' || (gameState.phase === 'finished' && gameState.player1.score === gameState.player2.score)) && !isP1Winner && !isP2Winner;

    // =========================================================================
    // 6. RENDERIZADO DE TABLERO 3D DE JUGADOR
    // =========================================================================
    const renderPlayerBoard = (playerKey, playerState, playerColor) => {
        const isPlayer1 = playerKey === 'player1';

        return (
            <div
                className={`memory-board-grid ${gridColumnsClass}`}
                role="grid"
                aria-label={`Tablero de ${playerState.name}`}
            >
                {playerState.board.map((card) => {
                    const isFlipped = card.isFlipped || card.isMatched || gameState.phase === 'preview';
                    const isMatched = card.isMatched;

                    const cardBackStyle = {
                        backgroundColor: cardBackBackgroundColor,
                        borderColor: `${playerColor}55`
                    };

                    const cardFrontGlowStyle = isMatched ? {
                        borderColor: playerColor,
                        boxShadow: `0 0 25px ${playerColor}, inset 0 0 15px ${playerColor}`
                    } : {};

                    return (
                        <div
                            key={card.cardIndex}
                            className={`memory-screen-card ${isFlipped ? 'is-flipped' : ''} ${isMatched ? (isPlayer1 ? 'matched-p1' : 'matched-p2') : ''}`}
                            style={{ '--player-color': playerColor }}
                        >
                            <div className="memory-screen-card__inner">
                                {/* Dorso de la carta (Boca abajo) */}
                                <div
                                    className="memory-screen-card__face memory-screen-card__face--back"
                                    style={cardBackStyle}
                                >
                                    {cardBackLogoUrl ? (
                                        <img
                                            src={cardBackLogoUrl}
                                            alt="Card Back Logo"
                                            className="memory-screen-card__back-logo"
                                            onError={(e) => {
                                                e.target.onerror = null;
                                                e.target.style.display = 'none';
                                            }}
                                        />
                                    ) : (
                                        <div className="memory-screen-card__back-pattern">
                                            <span>★</span>
                                        </div>
                                    )}
                                </div>

                                {/* Frente de la carta (Boca arriba) */}
                                <div
                                    className="memory-screen-card__face memory-screen-card__face--front"
                                    style={cardFrontGlowStyle}
                                >
                                    {card.imageUrl ? (
                                        <img
                                            src={card.imageUrl}
                                            alt={`Carta ${card.cardIndex + 1}`}
                                            className="memory-screen-card__img"
                                            onError={(e) => {
                                                e.target.onerror = null;
                                                e.target.src = DEFAULT_FALLBACK_LOGO_SVG;
                                            }}
                                        />
                                    ) : (
                                        <div className="memory-screen-card__empty-front">
                                            <span>{card.pairId !== null ? `PAR #${card.pairId}` : '★'}</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    };

    // =========================================================================
    // 7. RENDERIZADO PRINCIPAL DEL ESTADIO
    // =========================================================================
    return (
        <main
            className="memory-screen-game"
            style={{
                fontFamily: `'${chosenFontFamily}', 'Inter', sans-serif`,
                color: gameTextColor
            }}
            role="region"
            aria-label="Escenario de Memorama 1 vs 1"
        >
            {/* Fondo Dinámico GPU Layer */}
            {gameBackgroundType === 'image' && gameBackgroundUrl ? (
                <div
                    className="memory-screen-game__bg-image"
                    style={{ backgroundImage: `url(${gameBackgroundUrl})` }}
                />
            ) : (
                <div
                    className="memory-screen-game__bg-color"
                    style={{ backgroundColor: gameBackgroundColor }}
                />
            )}

            {/* Viñeta oscura de contraste */}
            <div className="memory-screen-game__vignette" />

            {/* Resplandor ambiental de estadio */}
            <div className="memory-screen-game__ambient-glow" />

            {/* -----------------------------------------------------------------
                HEADER DEL ESTADIO: LOGO CENTRAL
            ----------------------------------------------------------------- */}
            <header className="memory-screen-header">
                {/* Centro: Logo Oficial de la Marca / Club */}
                <div className="memory-screen-header__center-box">
                    <img
                        src={brandLogoUrl || '/img/page/adsl-five.png'}
                        alt="Marca Oficial"
                        className="memory-screen-header__brand-logo"
                        onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = '/img/page/adsl-five.png';
                        }}
                    />
                </div>
            </header>

            {/* -----------------------------------------------------------------
                BANNER NEÓN DE MEMORIZACIÓN (FASE PREVIEW)
            ----------------------------------------------------------------- */}
            {gameState.phase === 'preview' && (
                <div className="memory-screen-preview-banner">
                    <span className="memory-screen-preview-banner__icon">👀</span>
                    <span className="memory-screen-preview-banner__text">
                        ¡MEMORIZA LAS CARTAS! ({gameState.previewRemainingSeconds}s)
                    </span>
                    <span className="memory-screen-preview-banner__icon">👀</span>
                </div>
            )}

            {/* -----------------------------------------------------------------
                ESCENARIO SPLIT-SCREEN 1 VS 1 (COLUMNA J1 VS COLUMNA J2)
            ----------------------------------------------------------------- */}
            <div className="memory-screen-arena">
                {/* COLUMNA JUGADOR 1 */}
                <section
                    className={`memory-player-column memory-player-column--p1 ${isP1Winner ? 'is-winner' : ''}`}
                    style={{ '--player-accent': player1Color }}
                >
                    {/* Header del Jugador 1 */}
                    <div className="memory-player-column__header">
                        <div className="memory-player-column__avatar-box" style={{ borderColor: player1Color }}>
                            <img
                                src={player1Avatar || DEFAULT_AVATAR_P1_SVG}
                                alt={gameState.player1.name}
                                className="memory-player-column__avatar"
                                onError={(e) => {
                                    e.target.onerror = null;
                                    e.target.src = DEFAULT_AVATAR_P1_SVG;
                                }}
                            />
                        </div>
                        <div className="memory-player-column__meta">
                            <h2 className="memory-player-column__name" style={{ color: player1Color }}>
                                {gameState.player1.name}
                            </h2>
                            <div className="memory-player-column__stats">
                                <span className="memory-player-column__score-badge">
                                    Pares: {gameState.player1.score} / {pairsCount}
                                </span>
                                <span className="memory-player-column__flips-badge">
                                    Intentos: {gameState.player1.totalFlips}
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Tablero 3D J1 */}
                    {renderPlayerBoard('player1', gameState.player1, player1Color)}

                    {/* Sellos Cinemáticos de Fin de Partida */}
                    {gameState.phase === 'finished' && (
                        <div className="memory-stamp-overlay">
                            {isP1Winner && (
                                <div className="memory-stamp memory-stamp--winner">
                                    ¡GANADOR!
                                </div>
                            )}
                            {isP2Winner && (
                                <div className="memory-stamp memory-stamp--loser">
                                    DERROTA
                                </div>
                            )}
                            {isTie && (
                                <div className="memory-stamp memory-stamp--tie">
                                    ¡EMPATE!
                                </div>
                            )}
                        </div>
                    )}
                </section>

                {/* DIVISOR CENTRAL CON LUZ NEÓN */}
                <div className="memory-screen-arena__divider" />

                {/* COLUMNA JUGADOR 2 */}
                <section
                    className={`memory-player-column memory-player-column--p2 ${isP2Winner ? 'is-winner' : ''}`}
                    style={{ '--player-accent': player2Color }}
                >
                    {/* Header del Jugador 2 */}
                    <div className="memory-player-column__header">
                        <div className="memory-player-column__avatar-box" style={{ borderColor: player2Color }}>
                            <img
                                src={player2Avatar || DEFAULT_AVATAR_P2_SVG}
                                alt={gameState.player2.name}
                                className="memory-player-column__avatar"
                                onError={(e) => {
                                    e.target.onerror = null;
                                    e.target.src = DEFAULT_AVATAR_P2_SVG;
                                }}
                            />
                        </div>
                        <div className="memory-player-column__meta">
                            <h2 className="memory-player-column__name" style={{ color: player2Color }}>
                                {gameState.player2.name}
                            </h2>
                            <div className="memory-player-column__stats">
                                <span className="memory-player-column__score-badge">
                                    Pares: {gameState.player2.score} / {pairsCount}
                                </span>
                                <span className="memory-player-column__flips-badge">
                                    Intentos: {gameState.player2.totalFlips}
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Tablero 3D J2 */}
                    {renderPlayerBoard('player2', gameState.player2, player2Color)}

                    {/* Sellos Cinemáticos de Fin de Partida */}
                    {gameState.phase === 'finished' && (
                        <div className="memory-stamp-overlay">
                            {isP2Winner && (
                                <div className="memory-stamp memory-stamp--winner">
                                    ¡GANADOR!
                                </div>
                            )}
                            {isP1Winner && (
                                <div className="memory-stamp memory-stamp--loser">
                                    DERROTA
                                </div>
                            )}
                            {isTie && (
                                <div className="memory-stamp memory-stamp--tie">
                                    ¡EMPATE!
                                </div>
                            )}
                        </div>
                    )}
                </section>
            </div>

            {/* -----------------------------------------------------------------
                OVERLAY ARCADE COUNTDOWN 3..2..1
            ----------------------------------------------------------------- */}
            {gameState.phase === 'countdown' && typeof gameState.countdownNumber === 'number' && gameState.countdownNumber > 0 && (
                <div className="memory-screen-countdown-overlay">
                    <div className="memory-screen-countdown-overlay__number">
                        {gameState.countdownNumber}
                    </div>
                </div>
            )}

            {/* -----------------------------------------------------------------
                CONFETI EFÍMERO GPU 3D (3 SEGUNDOS GARANTIZADOS)
            ----------------------------------------------------------------- */}
            {confettiList.length > 0 && (
                <div className="memory-confetti-container" aria-hidden="true">
                    {confettiList.map((piece) => (
                        <div
                            key={piece.id}
                            className="memory-confetti-piece"
                            style={piece.style}
                        />
                    ))}
                </div>
            )}

            {/* Footer de Créditos */}
            <footer className="memory-screen-footer-credits">
                Hecho con ♥ por Interaktia
            </footer>
        </main>
    );
};

MemoryGame.propTypes = {
    payload: PropTypes.shape({
        type: PropTypes.string,
        state: PropTypes.object,
        data: PropTypes.object,
        payload: PropTypes.object,
        theme: PropTypes.object
    }),
    currentMatchState: PropTypes.object,
    theme: PropTypes.object,
    config: PropTypes.object,
    roomId: PropTypes.string
};

MemoryGame.defaultProps = {
    payload: null,
    currentMatchState: null,
    theme: null,
    config: null,
    roomId: null
};

export default MemoryGame;